/**
 * labelJournal — a durable write-ahead log for lap-label corrections.
 *
 * Every correction is appended here BEFORE it is dispatched to the native
 * bridge, and removed only after the write is confirmed AND read back
 * (see liveCorrection.ts). Entries that survive a process death are replayed
 * on boot. Backed by localStorage so it survives a force-kill relaunch.
 *
 * Pure + framework-free: no Svelte store, no bridge. The integration layer
 * (liveCorrection.ts) owns the reactive count store and the replay loop.
 */
import type { LapLabel } from "@contract/types";

export interface JournalEntry {
  commitId: number;
  workoutUUID: string | undefined;
  labels: LapLabel[];
  ts: number;
}

export const LABEL_JOURNAL_KEY = "swimset.labelJournal.v1";
/** Bound the log so a long offline streak can't grow it without limit. Oldest
 *  entries are dropped first; the newest user intent is always retained. */
export const JOURNAL_CAP = 50;

function write(entries: JournalEntry[]): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(LABEL_JOURNAL_KEY, JSON.stringify(entries));
  } catch {
    /* quota / private-mode / purged — degrades to in-session only (known debt) */
  }
}

export function readJournal(): JournalEntry[] {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(LABEL_JOURNAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as JournalEntry[];
  } catch {
    return [];
  }
}

/**
 * Append a correction to the journal. Each entry is a FULL replacement label
 * set for its swim (see commitLabelChange), so newest-per-swim is the correct
 * invariant: any existing entry for the same workoutUUID is superseded (not
 * kept alongside) before the new one is appended. This also caps the journal
 * at one entry per swim, and — critically — means a swim's stale, still-failed
 * entry can never survive to be replayed over a later successful correction.
 */
export function appendJournal(entry: JournalEntry): void {
  const entries = readJournal().filter((e) => e.workoutUUID !== entry.workoutUUID);
  entries.push(entry);
  // FIFO cap: keep the newest JOURNAL_CAP entries.
  const capped = entries.length > JOURNAL_CAP ? entries.slice(entries.length - JOURNAL_CAP) : entries;
  write(capped);
}

/**
 * Remove a confirmed entry. Matches on workoutUUID AND commitId together —
 * commitId alone is not a safe key: commitSeq is session-local and resets to
 * 0 on every relaunch, so a stranded entry from a prior session can share an
 * id with a fresh entry for an unrelated swim in the current session. Keying
 * removal on the pair closes that cross-session collision.
 */
export function removeJournal(workoutUUID: string | undefined, commitId: number): void {
  const entries = readJournal().filter((e) => !(e.workoutUUID === workoutUUID && e.commitId === commitId));
  write(entries);
}

export function journalCount(): number {
  return readJournal().length;
}

/** TEST-ONLY: clear the journal between test cases. */
export function _clearJournal(): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(LABEL_JOURNAL_KEY);
  } catch {
    /* ignore */
  }
}
